import React, { useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { isBrowser } from "@/utils/runtime";

const TocTocPortfolio = () => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  const portfolioImages = [
    {
      src: "/lovable-uploads/2944ddc1-4b3e-4aa1-8a52-215f0619bc2c.png",
      alt: "Área de lazer com piscina para Airbnb em Balneário Camboriú",
      category: "Área de lazer"
    },
    {
      src: "/lovable-uploads/0bf40fa0-33c5-401b-8b40-115110e05f5a.png",
      alt: "Apartamento de luxo com vista para o mar em Itajaí",
      category: "Residencial"
    },
    {
      src: "/lovable-uploads/484f8cd9-6dea-49d1-9bd4-bc39bf36c6e4.png",
      alt: "Resort com piscina em Camboriú - fotografia comercial",
      category: "Comercial"
    },
    {
      src: "/lovable-uploads/2972e843-ebe4-4775-8d5d-333ae298c654.png",
      alt: "Quarto elegante de imóvel em Porto Belo",
      category: "Residencial"
    },
    {
      src: "/lovable-uploads/011fcffd-6266-47da-b7ec-91396f3a26bb.png",
      alt: "Cobertura com piscina em Balneário Camboriú",
      category: "Área de lazer"
    },
    {
      src: "/lovable-uploads/de23c8d2-05b8-4009-a2bf-1c4637e23daf.png",
      alt: "Cozinha moderna de apartamento em Itajaí",
      category: "Residencial"
    },
    {
      src: "/lovable-uploads/1b94ad33-6f76-48f1-b058-c35405190cf2.png",
      alt: "Área externa para temporada em Itapema",
      category: "Área de lazer"
    },
    {
      src: "/lovable-uploads/3194f393-5b96-45db-8785-e619cb0eff32.png",
      alt: "Quarto de casa em Camboriú - fotografia imobiliária",
      category: "Residencial"
    }
  ];

  const openModal = (index: number) => {
    setSelectedImageIndex(index);
  };

  const closeModal = () => {
    setSelectedImageIndex(null);
  };

  const goToNext = () => {
    if (selectedImageIndex !== null) {
      setSelectedImageIndex((selectedImageIndex + 1) % portfolioImages.length);
    }
  };

  const goToPrevious = () => {
    if (selectedImageIndex !== null) {
      setSelectedImageIndex((selectedImageIndex - 1 + portfolioImages.length) % portfolioImages.length);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeModal();
    } else if (e.key === 'ArrowRight') {
      goToNext();
    } else if (e.key === 'ArrowLeft') {
      goToPrevious();
    }
  };

  React.useEffect(() => {
    if (!isBrowser) return;
    const handleKeyPress = (e: KeyboardEvent) => {
      if (selectedImageIndex !== null) {
        if (e.key === 'Escape') {
          closeModal();
        } else if (e.key === 'ArrowRight') {
          goToNext();
        } else if (e.key === 'ArrowLeft') {
          goToPrevious();
        }
      }
    };

    globalThis.document.addEventListener('keydown', handleKeyPress);
    return () => globalThis.document.removeEventListener('keydown', handleKeyPress);
  }, [selectedImageIndex]);

  return (
    <>
      <section className="py-24 bg-gray-50">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center mb-20 animate-[fadeInUp_0.8s_ease-out_0.2s_both]">
            <h2 className="text-4xl md:text-5xl font-bold text-[#333333] mb-6">
              Portfólio de imóveis fotografados em <span className="text-[#FF5605]">Balneário Camboriú</span>
            </h2>
            <p className="text-xl text-[#5B5A59] max-w-3xl mx-auto">
              Veja alguns dos imóveis que já fotografamos em Balneário Camboriú, Itajaí, Camboriú e Porto Belo. Resultados impressionantes que aceleram vendas e maximizam reservas em plataformas como Airbnb.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {portfolioImages.map((image, index) => (
              <div 
                key={index}
                className={`portfolio-card group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer animate-[fadeInUp_0.6s_ease-out_${0.2 * (index + 1)}s_both]`}
                onClick={() => openModal(index)}
              >
                <img 
                  src={image.src} 
                  alt={image.alt}
                  loading="lazy"
                  width="400"
                  height="320"
                  className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                
                <div className="absolute inset-0 bg-gradient-to-t from-[#333333]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <div className="text-sm font-medium text-[#FF5605] mb-1">
                    {image.category}
                  </div>
                  <div className="font-semibold">
                    {image.alt}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-16 animate-[fadeInUp_0.8s_ease-out_1s_both]">
            <Link
              to="/portfolio"
              className="inline-block bg-[#FF5605] hover:bg-[#E14D04] text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105"
            >
              Ver portfólio completo
            </Link>
          </div>
        </div>
      </section>

      {/* Modal de Galeria */}
      {selectedImageIndex !== null && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center animate-[fadeIn_0.3s_ease-out]"
          onClick={closeModal}
        >
          <div className="relative w-full h-full flex items-center justify-center p-4">
            {/* Botão de fechar */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10 bg-black bg-opacity-50 rounded-full p-2 animate-[fadeInDown_0.5s_ease-out]"
            >
              <X className="h-8 w-8" />
            </button>

            {/* Seta anterior */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              className="absolute left-4 text-white hover:text-gray-300 z-10 bg-black bg-opacity-50 rounded-full p-2 animate-[fadeInLeft_0.5s_ease-out_0.2s_both]"
            >
              <ChevronLeft className="h-8 w-8" />
            </button>

            {/* Seta próxima */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-4 text-white hover:text-gray-300 z-10 bg-black bg-opacity-50 rounded-full p-2 animate-[fadeInRight_0.5s_ease-out_0.2s_both]"
            >
              <ChevronRight className="h-8 w-8" />
            </button>

            {/* Imagem */}
            <div className="max-w-6xl max-h-full flex flex-col items-center">
              <img
                src={portfolioImages[selectedImageIndex].src}
                alt={portfolioImages[selectedImageIndex].alt}
                loading="lazy"
                decoding="async"
                width="1280"
                height="853"
                className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl animate-[scaleIn_0.4s_ease-out]"
                onClick={(e) => e.stopPropagation()}
              />
              
              {/* Informações da imagem */}
              <div className="text-white text-center mt-4 bg-black bg-opacity-80 rounded-lg p-4 animate-[fadeInUp_0.5s_ease-out_0.3s_both]">
                <span className="inline-block px-3 py-1 bg-[#FF5605] rounded-full text-sm font-medium mb-2">
                  {portfolioImages[selectedImageIndex].category}
                </span>
                <h3 className="text-xl font-bold mb-2">{portfolioImages[selectedImageIndex].alt}</h3>
                <p className="text-gray-400 text-sm">
                  {selectedImageIndex + 1} de {portfolioImages.length}
                </p>
              </div>
            </div>

            {/* Indicadores de navegação */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 bg-black bg-opacity-80 rounded-lg p-2 animate-[fadeInUp_0.5s_ease-out_0.4s_both]">
              {portfolioImages.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImageIndex(index);
                  }}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === selectedImageIndex ? 'bg-[#FF5605] scale-125' : 'bg-gray-400 hover:bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TocTocPortfolio;
